import {
  Antenna,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Wifi,
  WifiOff,
  Activity,
  Gauge,
  Cpu,
  HardDrive,
  Thermometer,
  Battery,
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  Signal,
  SignalHigh,
  SignalMedium,
  SignalLow,
  SignalZero,
  Zap,
  Database,
  Radio,
  RadioTower,
  Satellite,
  Navigation,
  Timer,
  Power,
  MemoryStick,
  HelpCircle,
  BatteryCharging,
  BatteryWarning,
  ArrowUpDown,
  AlertCircle,
} from 'lucide-react';
import { formatTimestamp, formatRelativeTime } from './dateUtils';

const RATE_FIELDS = ['can_0_msg_count', 'can_1_msg_count', 'can_2_msg_count'];

// Calculate per-minute delta for supported counter fields.
export const calculateCounterRate = (fieldKey, currentData, previousData) => {
  if (!RATE_FIELDS.includes(fieldKey)) {
    return null;
  }

  if (!currentData || !previousData) {
    return null;
  }

  const currentValue = parseInt(currentData[fieldKey]);
  const previousValue = parseInt(previousData[fieldKey]);
  const currentTime = currentData.last_update_time;
  const previousTime = previousData.last_update_time;

  if (
    isNaN(currentValue) ||
    isNaN(previousValue) ||
    currentValue < 0 ||
    previousValue < 0 ||
    !currentTime ||
    !previousTime
  ) {
    return null;
  }

  const timeDiffSec = currentTime - previousTime;
  if (timeDiffSec <= 0) {
    return null;
  }

  const countDiff = currentValue - previousValue;
  if (countDiff < 0) {
    return null;
  }

  return countDiff / (timeDiffSec / 60);
};

export const createCounterRateIndicator = (fieldKey, currentData, previousData) => {
  // Only calculate for supported rate fields
  if (!RATE_FIELDS.includes(fieldKey)) {
    return null;
  }

  // Must have current data
  if (!currentData || !currentData[fieldKey]) {
    return null;
  }

  const currentValue = parseInt(currentData[fieldKey]);
  const currentTime = currentData.last_update_time;

  // Current value must be greater than 0
  if (isNaN(currentValue) || currentValue <= 0 || !currentTime) {
    return null;
  }

  const success = { icon: CheckCircle, status: 'success', label: null };

  // Without a usable previous value there is no rate, only a green check
  if (!previousData || !previousData[fieldKey] || !previousData.last_update_time) {
    return success;
  }

  const previousValue = parseInt(previousData[fieldKey]);
  if (isNaN(previousValue) || previousValue <= 0 || previousValue > currentValue) {
    return success;
  }

  const rate = calculateCounterRate(fieldKey, currentData, previousData);
  if (rate === null) {
    return null;
  }

  if (rate === 0) {
    return { icon: AlertTriangle, status: 'warning', label: '0/s' };
  }

  return { icon: ArrowUpDown, status: 'success', label: `${(rate / 60).toFixed(2)}/s` };
};

// esp-idf twai_state_t values reported in can_0_state
export const TWAI_STATE_NAMES = {
  0: 'TWAI_STATE_STOPPED',
  1: 'TWAI_STATE_RUNNING',
  2: 'TWAI_STATE_BUS_OFF',
  3: 'TWAI_STATE_RECOVERING',
};

// Returns the twai_state_t number, or null if the value is not 0-3.
export const parseTwaiState = value => {
  const state = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return Number.isInteger(state) && TWAI_STATE_NAMES[state] ? state : null;
};

export const fieldNameMap = {
  last_update_time: 'Last Sync Time',
  version: 'Software Version',
  ntrip: 'NTRIP Status',
  gnss_last_location: 'GNSS Position (GGA)',
  gnss_last_gst: 'GNSS Accuracy (GST)',
  gnss_version: 'GNSS Firmware Version',
  uptime: 'Uptime (milliseconds)',
  ntp_time: 'NTP Time',
  ntp_last_updated: 'NTP Last Updated',
  can_0_msg_count: 'CAN 0 Message Count',
  can_1_msg_count: 'CAN 1 Message Count',
  can_2_msg_count: 'CAN 2 Message Count',
  battery_voltage: 'Battery Voltage',
  modem_rssi: 'Modem RSSI',
  modem_network_info: 'Modem Network Info',
};

export const fieldOrder = [
  'version',
  'last_update_time',
  'ntrip',
  'gnss_last_location',
  'gnss_last_gst',
  'uptime',
  'ntp_time',
  'ntp_last_updated',
  'can_0_msg_count',
  'can_1_msg_count',
  'can_2_msg_count',
];

export const getFriendlyFieldName = key => {
  if (fieldNameMap[key]) {
    return fieldNameMap[key];
  }

  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getStatusIndicator = (key, value, fullData = null) => {
  const lowerValue = typeof value === 'string' ? value.toLowerCase() : '';
  switch (key) {
    case 'ntrip': {
      if (lowerValue === 'streaming') {
        return {
          icon: Antenna,
          status: 'success',
          label: 'Good',
        };
      } else if (lowerValue === 'stopped') {
        return {
          icon: WifiOff,
          status: 'success',
          label: 'NO NEEDED',
        };
      } else if (['connecting', 'connected', 'disconnected'].includes(lowerValue)) {
        return {
          icon: Radio,
          status: 'warning',
          label: 'NO DATA',
        };
      } else {
        return {
          icon: WifiOff,
          status: 'error',
          label: 'NO CONNECTION',
        };
      }
    }

    case 'gnss_last_location': {
      if (!value || lowerValue === 'no gpgga data' || lowerValue === 'invalid gpgga format') {
        return {
          icon: MapPin,
          status: 'error',
          label: 'No GPS',
        };
      }
      if (typeof value === 'string' && lowerValue.includes('fix quality:')) {
        if (lowerValue.includes('rtk fixed')) {
          return {
            icon: Navigation,
            status: 'success',
            label: 'RTK Fixed',
          };
        } else if (lowerValue.includes('invalid') || lowerValue.includes('unknown')) {
          return {
            icon: HelpCircle,
            status: 'error',
            label: 'NO POSITION',
          };
        } else {
          return {
            icon: Satellite,
            status: 'warning',
            label: 'GPS Fix',
          };
        }
      }
      return {
        icon: MapPin,
        status: 'error',
        label: 'NO POSITION',
      };
    }

    case 'gnss_last_gst': {
      if (!value || lowerValue === 'no gngst data' || lowerValue === 'invalid gngst format') {
        return {
          icon: XCircle,
          status: 'error',
          label: 'No Data',
        };
      }
      if (typeof value === 'string') {
        const latMatch = value.match(/Latitude Std Dev \(1σ\):\s*([\d.]+)/);
        const lonMatch = value.match(/Longitude Std Dev \(1σ\):\s*([\d.]+)/);
        const altMatch = value.match(/Altitude Std Dev \(1σ\):\s*([\d.]+)/);

        if (latMatch && lonMatch && altMatch) {
          const latStd = parseFloat(latMatch[1]);
          const lonStd = parseFloat(lonMatch[1]);
          const altStd = parseFloat(altMatch[1]);

          if (latStd < 0.05 && lonStd < 0.05 && altStd < 0.05) {
            return {
              icon: CheckCircle,
              status: 'success',
              label: 'High Precision',
            };
          }
        }
      }
      return {
        icon: AlertTriangle,
        status: 'error',
        label: 'Low Precision',
      };
    }

    case 'ntp_last_updated': {
      if (parseInt(value.charAt(0)) >= 2) {
        return {
          icon: Clock,
          status: 'success',
          label: 'Synced',
        };
      } else {
        return {
          icon: Timer,
          status: 'error',
          label: 'Not Synced',
        };
      }
    }

    case 'cpu_usage':
    case 'cpu_percent': {
      const usage = parseFloat(value) || 0;
      if (usage < 70) {
        return {
          icon: Cpu,
          status: 'success',
          label: 'Normal',
        };
      } else if (usage < 90) {
        return {
          icon: Gauge,
          status: 'warning',
          label: 'High',
        };
      } else {
        return {
          icon: Activity,
          status: 'error',
          label: 'Critical',
        };
      }
    }

    case 'memory_usage':
    case 'mem_percent': {
      const usage = parseFloat(value) || 0;
      if (usage < 80) {
        return {
          icon: MemoryStick,
          status: 'success',
          label: 'Normal',
        };
      } else if (usage < 95) {
        return {
          icon: HardDrive,
          status: 'warning',
          label: 'High',
        };
      } else {
        return {
          icon: Database,
          status: 'error',
          label: 'Critical',
        };
      }
    }

    case 'temperature':
    case 'temp': {
      const temp = parseFloat(value) || 0;
      if (temp < 60) {
        return {
          icon: Thermometer,
          status: 'info',
          label: 'Cool',
        };
      } else if (temp < 80) {
        return {
          icon: Thermometer,
          status: 'warning',
          label: 'Warm',
        };
      } else {
        return {
          icon: Zap,
          status: 'error',
          label: 'Hot',
        };
      }
    }

    case 'battery':
    case 'battery_level': {
      const level = parseFloat(value) || 0;
      if (level > 40) {
        return {
          icon: Battery,
          status: 'success',
          label: 'Good',
        };
      } else if (level > 20) {
        return {
          icon: Battery,
          status: 'warning',
          label: 'Low',
        };
      } else {
        return {
          icon: Power,
          status: 'error',
          label: 'Critical',
        };
      }
    }

    case 'network':
    case 'connection': {
      if (lowerValue === 'connected' || lowerValue === 'online') {
        return {
          icon: Wifi,
          status: 'success',
          label: 'Connected',
        };
      } else {
        return {
          icon: WifiOff,
          status: 'error',
          label: 'Disconnected',
        };
      }
    }

    case 'signal_strength':
    case 'signal': {
      const strength = parseFloat(value) || 0;
      if (strength > -60) {
        return {
          icon: Signal,
          status: 'success',
          label: 'Strong',
        };
      } else if (strength > -80) {
        return {
          icon: Signal,
          status: 'warning',
          label: 'Moderate',
        };
      } else {
        return {
          icon: Signal,
          status: 'error',
          label: 'Weak',
        };
      }
    }

    case 'battery_voltage': {
      // Check if the value is not a valid number (like "Err")
      const voltage = parseFloat(value);
      if (isNaN(voltage) || typeof value !== 'string') {
        return {
          icon: XCircle,
          status: 'error',
          label: 'Error',
        };
      }

      // Get battery powered status from full data
      const isBatteryPowered = fullData?.is_battery_powered === true;

      // If battery powered, show battery percentage based on voltage
      if (isBatteryPowered) {
        let percentage;
        let status;

        if (voltage >= 4.1) {
          percentage = '100%';
          status = 'success'; // Green
        } else if (voltage >= 4) {
          percentage = '90%';
          status = 'success'; // Green
        } else if (voltage >= 3.98) {
          percentage = '80%';
          status = 'success'; // Green
        } else if (voltage >= 3.92) {
          percentage = '70%';
          status = 'success'; // Green
        } else if (voltage >= 3.85) {
          percentage = '60%';
          status = 'success'; // Green
        } else if (voltage >= 3.8) {
          percentage = '50%';
          status = 'success'; // Green
        } else if (voltage >= 3.75) {
          percentage = '40%';
          status = 'warning'; // Yellow
        } else if (voltage >= 3.68) {
          percentage = '30%';
          status = 'warning'; // Yellow
        } else if (voltage >= 3.6) {
          percentage = '20%';
          status = 'warning'; // Yellow
        } else if (voltage >= 3.4) {
          percentage = '10%';
          status = 'error'; // Red
        } else {
          percentage = '0%';
          status = 'error'; // Red
        }

        // Determine icon based on percentage
        const percentageValue = parseInt(percentage);
        let icon = Battery;
        if (percentageValue > 70) {
          icon = BatteryFull;
        } else if (percentageValue > 40) {
          icon = BatteryMedium;
        } else if (percentageValue > 20) {
          icon = BatteryLow;
        }

        return {
          icon: icon,
          status: status,
          label: percentage,
        };
      } // Get charging status from full data if available
      const isCharging = fullData?.is_battery_charging === true;

      // If charging, show green "Charging"
      if (isCharging) {
        return {
          icon: BatteryCharging,
          status: 'success',
          label: 'Charging',
        };
      }

      // If not charging
      if (!isCharging) {
        // Low voltage and not charging - "No Battery"
        if (voltage < 2.5) {
          return {
            icon: BatteryWarning,
            status: 'error',
            label: 'No Battery',
          };
        } else if (voltage > 4.0) {
          // High voltage and not charging - "Full Charged"
          return {
            icon: BatteryCharging,
            status: 'success',
            label: 'Full Charged',
          };
        } else if (voltage > 2.5 && voltage <= 4.0) {
          // Medium voltage and not charging - "Not Charging"
          return {
            icon: BatteryWarning,
            status: 'warning',
            label: 'Not Charging',
          };
        }
      }

      return null;
    }

    case 'modem_rssi': {
      const rssi = parseInt(value);

      // Check if the value is out of valid range (0-32)
      if (isNaN(rssi) || rssi < 0 || rssi > 32) {
        return {
          icon: XCircle,
          status: 'error',
          label: 'Error',
        };
      }

      // Determine signal quality and icon
      // RSSI ranges with 5 signal bars
      let status;
      let icon;
      let label;
      if (rssi === 0) {
        status = 'error';
        icon = XCircle;
        label = 'No Signal';
      } else if (1 <= rssi && rssi < 7) {
        status = 'warning';
        icon = SignalZero;
        label = 'Poor (1/5)';
      } else if (7 <= rssi && rssi < 9) {
        status = 'warning';
        icon = SignalLow;
        label = 'Fair (2/5)';
      } else if (9 <= rssi && rssi < 12) {
        status = 'success';
        icon = SignalMedium;
        label = 'Good (3/5)';
      } else if (12 <= rssi && rssi < 17) {
        status = 'success';
        icon = SignalHigh;
        label = 'Very good (4/5)';
      } else if (17 <= rssi && rssi <= 32) {
        status = 'success';
        icon = Signal;
        label = 'Excellent (5/5)';
      } else {
        icon = XCircle;
        status = 'error';
        label = 'Error';
      }

      return {
        icon: icon,
        status: status,
        label: label,
      };
    }

    case 'modem_network_info': {
      if (typeof value !== 'string') {
        return {
          icon: XCircle,
          status: 'error',
          label: 'Error',
        };
      }

      const upperValue = value.toUpperCase();
      let status;
      let label;

      if (upperValue.includes('NR')) {
        status = 'success';
        label = '5G';
      } else if (upperValue.includes('LTE')) {
        status = 'success';
        label = '4G';
      } else if (upperValue.includes('WCDMA')) {
        status = 'warning';
        label = '3G';
      } else if (upperValue.includes('GSM')) {
        status = 'warning';
        label = '2G';
      } else {
        status = 'warning';
        label = 'Unknown';
      }

      return {
        icon: RadioTower,
        status: status,
        label: label,
      };
    }

    case 'can_0_state': {
      // value is already formatted for display; judge by the raw value
      const state = parseTwaiState(fullData ? fullData.can_0_state : value);
      if (state === 1) {
        return { icon: CheckCircle, status: 'success' };
      }
      if (state === 3) {
        return { icon: AlertCircle, status: 'warning' };
      }
      if (state === 0 || state === 2) {
        return { icon: AlertCircle, status: 'error' };
      }
      return null;
    }

    case 'gnss_version': {
      // "Upgrading to <type> <version>" while the hub flashes the GNSS receiver
      if (typeof value !== 'string' || !value.startsWith('Upgrading to ')) {
        return null;
      }
      return {
        icon: Satellite,
        status: 'warning',
        label: 'UPGRADING',
      };
    }

    default:
      if (typeof value === 'boolean') {
        return value
          ? {
              icon: CheckCircle,
              status: 'success',
              label: 'Active',
            }
          : {
              icon: XCircle,
              status: 'error',
              label: 'Inactive',
            };
      }
      return null;
  }
};

export const formatMemInfo = memInfo => {
  if (!memInfo || typeof memInfo !== 'object') {
    return '';
  }

  const entries = Object.entries(memInfo);
  if (entries.length === 0) {
    return '';
  }

  const sortedEntries = entries.sort((a, b) => parseInt(b[0]) - parseInt(a[0]));

  return sortedEntries
    .map(([timestamp, content]) => {
      const formattedTime = formatTimestamp(parseInt(timestamp));
      return `${formattedTime}:\n  ${content}`;
    })
    .join('\n\n');
};

export const formatTaskInfoText = taskInfoText => {
  if (!taskInfoText || typeof taskInfoText !== 'string') {
    return '';
  }

  return taskInfoText
    .replace(/\\t/g, '\t')
    .replace(/\\r\\n/g, '\n')
    .replace(/\r\n/g, '\n');
};

export const getTaskInfoTimestamps = taskInfo => {
  if (!taskInfo || typeof taskInfo !== 'object') {
    return [];
  }

  const sortedTimestamps = Object.keys(taskInfo).sort((a, b) => parseInt(b) - parseInt(a));

  return sortedTimestamps.map(timestamp => ({
    value: timestamp,
    label: formatTimestamp(parseInt(timestamp)),
  }));
};

export const getTitleOnlineStatusText = (isOnline, lastSyncTime) => {
  if (isOnline) {
    return 'Online';
  }
  if (lastSyncTime && typeof lastSyncTime === 'number' && lastSyncTime > 0) {
    const relativeTime = formatRelativeTime(lastSyncTime);
    return `Offline (Last Seen: ${relativeTime})`;
  }
  return 'Offline';
};
